import React, { type RefObject } from "react";

export const useHorizontalScroll = (
  scrollRef: RefObject<HTMLDivElement | null>,
  dataLength?: number,
) => {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [canScrollHorizontally, setCanScrollHorizontally] =
    React.useState(false);

  React.useEffect(() => {
    const element = scrollRef.current;

    if (!element) {
      return;
    }

    // Find all potential scrolling elements
    const potentialScrollers = [
      element,
      ...Array.from(
        element.querySelectorAll(
          '.overflow-auto, .overflow-x-auto, [style*="overflow"], [style*="overflow-x"]',
        ),
      ),
    ] as HTMLElement[];

    const computeState = () => {
      let anyScrollable = false;
      let anyScrolled = false;

      for (const scrollingElement of potentialScrollers) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollingElement;
        const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);

        if (maxScrollLeft > 0) anyScrollable = true;

        if (scrollLeft > 0) anyScrolled = true;

        // Early exit if both true
        if (anyScrollable && anyScrolled) break;
      }

      setCanScrollHorizontally(anyScrollable);
      setIsScrolled(anyScrolled);
    };

    const handleScroll = () => {
      computeState();
    };

    // Add scroll listeners to all potential scrollers
    const scrollHandlers = potentialScrollers.map((scrollingElement) => {
      const handler = handleScroll;
      scrollingElement.addEventListener("scroll", handler);

      return { element: scrollingElement, handler };
    });

    // Initial checks (immediate, rAF, and timeout for late layout)
    computeState();

    const rafId = requestAnimationFrame(() => {
      computeState();
    });

    const timeoutId = window.setTimeout(() => {
      computeState();
    }, 150);

    // Also check on resize
    const resizeObserver = new ResizeObserver(() => {
      computeState();
    });

    potentialScrollers.forEach((scrollingElement) => {
      resizeObserver.observe(scrollingElement);
    });

    // Observe DOM mutations inside the container to catch late content width changes
    const mutationObserver = new MutationObserver(() => {
      computeState();
    });

    mutationObserver.observe(element, {
      subtree: true,
      childList: true,
      attributes: true,
      characterData: false,
    });

    return () => {
      scrollHandlers.forEach(({ element: scrollingElement, handler }) => {
        scrollingElement.removeEventListener("scroll", handler);
      });
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [dataLength, scrollRef]);

  return { isScrolled, canScrollHorizontally };
};
